import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, resolveAudioUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [demos, setDemos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("demos");

  const loadDemos = () => api.get("/demos").then((r) => setDemos(r.data || []));
  const loadMessages = () => api.get("/admin/messages").then((r) => setMessages(r.data || []));

  useEffect(() => {
    loadDemos();
    loadMessages();
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <main className="min-h-screen px-6 md:px-12 py-10" data-testid="admin-dashboard-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#F25C32] mb-3">— Panel administrador</div>
            <h1 className="heading-display text-4xl md:text-5xl text-[#F5F5F0]">
              Hola, {user?.name || "Santiago"}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF] hover:text-[#F25C32] transition-colors"
              data-testid="admin-view-site"
            >
              Ver sitio
            </Link>
            <button
              onClick={onLogout}
              className="text-xs tracking-[0.25em] uppercase text-[#F5F5F0]/80 hover:text-[#F25C32] transition-colors"
              data-testid="admin-logout"
            >
              Cerrar sesión
            </button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#111827] border border-white/10">
            <TabsTrigger value="demos" data-testid="tab-demos">
              Demos ({demos.length})
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              Mensajes ({messages.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="demos" className="mt-8">
            <DemosTab demos={demos} onChange={loadDemos} />
          </TabsContent>

          <TabsContent value="messages" className="mt-8">
            <MessagesTab messages={messages} onChange={loadMessages} />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function DemosTab({ demos, onChange }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [order, setOrder] = useState(0);
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title || !category) {
      toast.error("Título y categoría son obligatorios");
      return;
    }
    if (!file && !externalUrl) {
      toast.error("Sube un archivo o ingresa una URL");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("category", category);
      fd.append("order", String(order || 0));
      if (file) fd.append("file", file);
      if (externalUrl) fd.append("external_url", externalUrl);
      await api.post("/admin/demos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Demo agregado");
      setTitle(""); setCategory(""); setOrder(0); setFile(null); setExternalUrl("");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir demo");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este demo?")) return;
    try {
      await api.delete(`/admin/demos/${id}`);
      toast.success("Demo eliminado");
      onChange();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form onSubmit={onSubmit} className="lg:col-span-5 bg-[#111827] border border-white/10 rounded-lg p-6 space-y-4" data-testid="demo-upload-form">
        <h3 className="heading-display text-2xl text-[#F5F5F0]">Agregar demo</h3>

        <div>
          <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">Título</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="editorial-input" data-testid="demo-input-title" />
        </div>
        <div>
          <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">Categoría</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} className="editorial-input" placeholder="Trailer, Comercial, Radio…" data-testid="demo-input-category" />
        </div>
        <div>
          <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">Orden</label>
          <input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} className="editorial-input" data-testid="demo-input-order" />
        </div>

        <div className="pt-4">
          <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF] block mb-2">Archivo MP3</label>
          <input
            type="file"
            accept="audio/mpeg,audio/mp3,.mp3,.wav,.m4a,.ogg"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm text-[#F5F5F0] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-widest file:bg-[#F25C32] file:text-white hover:file:bg-[#D94E28] file:cursor-pointer"
            data-testid="demo-input-file"
          />
        </div>

        <div>
          <label className="text-xs tracking-[0.25em] uppercase text-[#9CA3AF]">O URL externa</label>
          <input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} className="editorial-input" placeholder="https://…" data-testid="demo-input-url" />
        </div>

        <button
          type="submit"
          disabled={uploading}
          className="w-full bg-[#F25C32] hover:bg-[#D94E28] text-white rounded-full px-8 py-3 uppercase tracking-[0.2em] text-xs font-bold transition-all disabled:opacity-50"
          data-testid="demo-submit"
        >
          {uploading ? "Subiendo…" : "Agregar demo"}
        </button>
      </form>

      <div className="lg:col-span-7" data-testid="admin-demos-list">
        <h3 className="heading-display text-2xl text-[#F5F5F0] mb-4">Demos publicados</h3>
        {demos.length === 0 && (
          <div className="text-[#9CA3AF] text-sm">Aún no hay demos.</div>
        )}
        <div className="space-y-3">
          {demos.map((d) => (
            <div key={d.id} className="bg-[#111827] border border-white/10 rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`admin-demo-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[#F5F5F0] font-bold truncate">{d.title}</div>
                <div className="text-xs text-[#F25C32] tracking-[0.2em] uppercase">{d.category} · #{d.order}</div>
                <audio controls src={resolveAudioUrl(d.url)} className="mt-2 w-full max-w-sm" />
              </div>
              <button
                onClick={() => onDelete(d.id)}
                className="text-xs tracking-[0.2em] uppercase text-red-400 hover:text-red-300"
                data-testid={`delete-demo-${d.id}`}
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MessagesTab({ messages, onChange }) {
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Mensaje eliminado");
      onChange();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (messages.length === 0) {
    return <div className="text-[#9CA3AF] text-sm" data-testid="empty-messages">Aún no hay mensajes.</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-messages-list">
      {messages.map((m) => (
        <div key={m.id} className="bg-[#111827] border border-white/10 rounded-lg p-5" data-testid={`admin-message-${m.id}`}>
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="text-[#F5F5F0] font-bold">{m.name}</div>
              <a href={`mailto:${m.email}`} className="text-xs text-[#F25C32]">{m.email}</a>
              <div className="text-xs text-[#9CA3AF] mt-1">
                {new Date(m.created_at).toLocaleString("es-CL")}
                {" · "}
                <span className={m.email_sent ? "text-green-400" : "text-yellow-400"}>
                  {m.email_sent ? "Email enviado" : "Solo guardado"}
                </span>
              </div>
            </div>
            <button
              onClick={() => onDelete(m.id)}
              className="text-xs tracking-[0.2em] uppercase text-red-400 hover:text-red-300"
              data-testid={`delete-message-${m.id}`}
            >
              Eliminar
            </button>
          </div>
          <div className="text-sm text-[#F25C32] tracking-[0.15em] uppercase mb-2">{m.subject}</div>
          <p className="text-[#F5F5F0]/85 whitespace-pre-wrap leading-relaxed">{m.message}</p>
        </div>
      ))}
    </div>
  );
}
